// FLAG:useDocuAi — the receipt variant of the single-file upload chain, extracted from
// fileService.startSingleUpload so its ORDERING is unit-testable without the store, axios or the
// full duck graph. Every dependency is injected.
//
// The ordering is the whole point (RC2). The legacy chain does:
//     upload POST → mark the row finished:true → THEN call complete-upload
// which frees the file's concurrency slot before the most expensive call in the chain has even
// started. On a 50-150 file drop that produced unbounded concurrent complete-uploads — each of
// which fetches the entire file back from the DMS and writes it as a Postgres large object inside
// its request transaction. Here `finished` is set only after complete-upload has resolved, so the
// slot covers the whole chain.
//
// Legacy sources never reach this function; startSingleUpload keeps its original body for them.

export type ReceiptSingleUploadDeps = {
  // Sends the bytes. Already wrapped in the retry policy by the caller.
  postFile: () => Promise<{ data?: { result?: { minioId?: string } } }>,
  // Calls POST /api/dms-upload/complete-upload.
  completeUpload: (args: { minioId: string }) => Promise<unknown>,
  // dispatch(updatePending({ uploadToken, ...patch }))
  patchPending: (patch: Record<string, unknown>) => void,
  // onUploadError(error, { uploadToken })
  reportError: (error: unknown) => void,
  // Reason used when the DMS reports neither an object nor an error.
  uploadFailedMessage: string,
}

export const runReceiptSingleUpload = async({
  postFile,
  completeUpload,
  patchPending,
  reportError,
  uploadFailedMessage,
}: ReceiptSingleUploadDeps): Promise<void> => {
  let minioId: string | undefined

  try {
    const response = await postFile()
    minioId = response?.data?.result?.minioId
  } catch (error) {
    // Mirrors the legacy special case: some DMS rejections still carry a usable minioId, meaning
    // the object did land. Complete it rather than reporting a failure.
    minioId = (error as { response?: { data?: { minioId?: string } } })?.response?.data?.minioId
    if (!minioId) {
      reportError(error)
      return
    }
  }

  if (!minioId) {
    // No object in the DMS and no error to report. complete-upload would reject this with a 400 on
    // the missing minioId, so fail here with the honest reason instead of a wasted round-trip.
    reportError({ response: { data: { message: uploadFailedMessage } } })
    return
  }

  // Bytes are in the DMS. `completing` — deliberately NOT `finished` — so the flyout keeps showing
  // the row as working and the scheduler keeps holding its slot.
  patchPending({ maxPartNumber: 1, progress: 100, minioId, completing: true, running: true })

  try {
    await completeUpload({ minioId })
    patchPending({ finished: true, completing: false, running: false, error: null })
  } catch (error) {
    patchPending({ completing: false, running: false })
    reportError(error)
  }
}
